// The dock mini player's text and buttons beside its media (Figma Android / Dock / Mini player 4304:10630, web screens
// 4308:11316 / 4308:11560): Video → a column beside the 200×200 player ("+N" and open on top, titles, play-pause and
// stop at the bottom); Audio → one row after the art (titles, "+N", play-pause, stop, open). A click on the titles
// opens the dock too.
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ChevronUp, CircleStop, Pause, Play } from "lucide-react";

export interface DockMini {
  /** How many other players stay in the dock ("+N"). */
  others: number;
  /** Back to the floating dock. */
  onOpen(): void;
}

interface Props {
  mini: DockMini;
  title: string;
  line: string;
  video: boolean;
  playing: boolean;
  /** Null: no play-pause (a watched cast follows its caster). */
  onTogglePlay: (() => void) | null;
  /** Null: the card brings its own stop (`stopNode`). */
  onStop: (() => void) | null;
  stopLabel?: string;
  /** The card's own controls instead of play-pause / stop (casts: confirm-to-stop, hide). */
  controls?: ReactNode;
  /** Buttons before play-pause (the wall card's Go to post). */
  actions?: ReactNode;
}

const plainBtn = "size-10 shrink-0 rounded-full flex items-center justify-center text-[var(--foreground)] hover:bg-[var(--bg-hover)]";

export default function MiniPlayerInfo({ mini, title, line, video, playing, onTogglePlay, onStop, stopLabel, controls, actions }: Props) {
  const { t } = useTranslation();
  const showLabel = t("dock_show", "Show the stream dock");
  const titles = (
    <button
      type="button"
      data-testid="dock-mini-title"
      onClick={mini.onOpen}
      aria-label={showLabel}
      className={`flex min-w-0 flex-col gap-0.5 text-left ${video ? "" : "flex-1"}`}
    >
      <span className={`text-sm font-bold leading-tight text-[var(--foreground)] ${video ? "line-clamp-2" : "truncate"}`}>{title}</span>
      <span className={`text-xs leading-[1.3] text-[var(--foreground-muted)] ${video ? "line-clamp-2" : "truncate"}`}>{line}</span>
    </button>
  );
  const chip = mini.others > 0 && (
    <button
      type="button"
      data-testid="dock-mini-more"
      onClick={mini.onOpen}
      aria-label={t("dock_more_players", "{{count}} more players stay in the dock", { count: mini.others })}
      className="min-w-8 shrink-0 rounded-full bg-[var(--bg-elevated)] px-2 py-0.5 text-sm font-bold text-[var(--foreground)]"
    >
      {t("dock_mini_more", "+{{count}}", { count: mini.others })}
    </button>
  );
  const open = (
    <button type="button" data-testid="dock-mini-open" onClick={mini.onOpen} aria-label={showLabel} title={showLabel} className={plainBtn}>
      <ChevronUp size={20} aria-hidden="true" />
    </button>
  );
  const buttons = (
    <>
      {actions}
      {onTogglePlay && (
        <button
          type="button"
          data-testid="dock-mini-play"
          onClick={onTogglePlay}
          aria-label={playing ? t("dock_pause", "Pause") : t("dock_play", "Play")}
          title={playing ? t("dock_pause", "Pause") : t("dock_play", "Play")}
          className="size-9 shrink-0 rounded-full bg-[var(--primary)] text-[var(--primary-foreground)] flex items-center justify-center"
        >
          {playing ? <Pause size={20} aria-hidden="true" /> : <Play size={20} aria-hidden="true" />}
        </button>
      )}
      {onStop && (
        <button type="button" data-testid="dock-mini-stop" onClick={onStop} aria-label={stopLabel ?? t("dock_stop", "Stop")} title={stopLabel ?? t("dock_stop", "Stop")} className={plainBtn}>
          <CircleStop size={20} aria-hidden="true" />
        </button>
      )}
      {controls}
    </>
  );
  if (video)
    return (
      <div data-testid="dock-mini-info" className="flex min-w-0 flex-1 flex-col gap-2 self-stretch">
        <div className="flex items-center">
          {chip}
          <span className="flex-1" />
          {open}
        </div>
        {titles}
        <span className="flex-1" />
        <div className="flex flex-wrap items-center gap-2">{buttons}</div>
      </div>
    );
  return (
    <div data-testid="dock-mini-info" className="flex min-w-0 flex-1 items-center gap-2">
      {titles}
      {chip}
      {buttons}
      {open}
    </div>
  );
}
